import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [bounceActive, setBounceActive] = useState(false);
  const [counters, setCounters] = useState({
    projects: 0,
    clients: 0,
    experience: 0,
    satisfaction: 0
  });

  const sectionRef = useRef(null);

  const stats = [
    { id: 'projects', value: 32, label: 'Projects Completed', icon: 'CheckCircle', suffix: '+', color: 'primary' },
    { id: 'clients', value: 19, label: 'Happy Clients', icon: 'Users', suffix: '+', color: 'accent' },
    { id: 'experience', value: 3, label: 'Years Experience', icon: 'Award', suffix: '+', color: 'success' },
    { id: 'satisfaction', value: 98, label: 'Client Satisfaction', icon: 'Heart', suffix: '%', color: 'error' }
  ];

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) setIsVisible(true);
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => sectionRef.current && observer.unobserve(sectionRef.current);
  }, [isVisible]);

  // Trigger bounce for 2 seconds
  useEffect(() => {
    if (isVisible) {
      setBounceActive(true);
      const timer = setTimeout(() => setBounceActive(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Smooth Counter Animation
  useEffect(() => {
    if (!isVisible) return;

    stats.forEach((stat, index) => {
      setTimeout(() => {
        let start = 0;
        const end = stat.value;
        const duration = 2000;
        const startTime = performance.now();

        const updateCounter = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easeProgress = progress < 0.5
            ? 2 * progress * progress
            : -1 + (4 - 2 * progress) * progress;
          const value = Math.floor(easeProgress * end);

          setCounters(prev => ({ ...prev, [stat.id]: value }));

          if (progress < 1) requestAnimationFrame(updateCounter);
          else setCounters(prev => ({ ...prev, [stat.id]: end }));
        };

        requestAnimationFrame(updateCounter);
      }, index * 300);
    });
  }, [isVisible]);

  const getColorClasses = (color) => {
    switch (color) {
      case 'primary': return { bg: 'bg-primary-50', icon: 'text-primary', border: 'border-primary-600' };
      case 'accent': return { bg: 'bg-accent-50', icon: 'text-accent', border: 'border-accent-600' };
      case 'success': return { bg: 'bg-success-50', icon: 'text-success', border: 'border-success-600' };
      case 'error': return { bg: 'bg-error-50', icon: 'text-error', border: 'border-error-600' };
      default: return { bg: 'bg-primary-50', icon: 'text-primary', border: 'border-primary-200' };
    }
  };

  return (
    <section ref={sectionRef} className="py-8 bg-gradient-to-br from-primary-50 to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className={`text-4xl sm:text-5xl font-extrabold text-text-primary mb-4 ${bounceActive ? 'animate-bounce' : ''}`}>
            Our Achievements
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Numbers that speak for our commitment to excellence and client satisfaction.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => {
            const colorClasses = getColorClasses(stat.color);

            return (
              <div
                key={stat.id}
                className={`
                group relative bg-surface border-2 ${colorClasses.border} rounded-2xl p-8 text-center
                transform transition-all duration-700 ease-out shadow-lg
                ${isVisible ? 'translate-x-0 opacity-100 scale-100' : '-translate-x-24 opacity-0 scale-95'}
              `}
                style={{ transitionDelay: `${index * 300}ms` }}
              >
                <div className={`absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-20 ${colorClasses.bg} animate-pulse`}></div>

                <div className={`w-16 h-16 ${colorClasses.bg} rounded-xl flex items-center justify-center mx-auto mb-6 transform transition-transform duration-500 group-hover:scale-125`}>
                  <Icon name={stat.icon} size={36} className={`${colorClasses.icon} ${bounceActive ? 'animate-bounce' : ''}`} />
                </div>

                <div className="mb-4">
                  <span className="text-4xl sm:text-5xl font-extrabold text-text-primary transition-transform duration-500 transform group-hover:scale-110">
                    {counters[stat.id]}
                  </span>
                  <span className="text-2xl font-bold text-text-primary">{stat.suffix}</span>
                </div>

                <p className="text-text-secondary font-medium">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-10 text-center">
          <div className="bg-surface rounded-3xl p-10 border border-border shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                { icon: 'Clock', color: 'text-primary', title: 'On-Time Delivery', desc: 'Every project delivered on schedule' },
                { icon: 'Shield', color: 'text-success', title: 'Quality Assurance', desc: 'Rigorous testing and quality control' },
                { icon: 'Headphones', color: 'text-accent', title: '24/7 Support', desc: 'Round-the-clock technical support' },
              ].map((info, index) => (
                <div
                  key={info.title}
                  className={`
                  flex items-center justify-center space-x-4 p-4 rounded-lg transition-transform duration-700
                  ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}
                  hover:scale-105
                `}
                  style={{ transitionDelay: `${index * 300}ms` }}
                >
                  <Icon name={info.icon} size={28} className={`${info.color} ${bounceActive ? 'animate-bounce' : ''}`} />
                  <div>
                    <p className="font-semibold text-text-primary">{info.title}</p>
                    <p className="text-sm text-text-secondary">{info.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

};

export default StatsSection;